import { useCallback, useEffect, useState } from "react";
import { fetchAffiliateLinks, fetchAffiliateStats } from "./affiliateLinks";
import AffiliateEarnModal from "./AffiliateEarnModal";
import { money, whenever } from "./affiliateMoney";
import Pagination from "./Pagination";
import { InlineLoader, Spinner } from "./PageLoader";

/**
 * "My earnings" — a page of its own for what a member has earned by sharing.
 *
 * ── Why this is a page and not the popup it used to be ──────────────────────
 * The figures started out at the bottom of the sharing-links popup, because
 * that was the only place they could go. Then members started selling: eighty
 * sales across thirty products does not fit in a 32rem popup, and the thing a
 * member opens every week ended up underneath the thing they open once.
 *
 * So it swapped round. The links are the one-off — get them, copy them, close
 * it — and they keep the popup. The money is what a member comes back for, so
 * it gets the page: a URL they can bookmark, a width that fits a table, and
 * room to say which products are actually selling.
 *
 * ── Still no account anywhere else ──────────────────────────────────────────
 * This member has never signed in to the platform that tracks their
 * commission, does not know it exists, and has no password for it. That is
 * deliberate, and it is exactly why this page has to exist: every other
 * affiliate in that system can go and look at what they are owed, and without
 * this one, this member has nowhere to look.
 *
 * Nothing here names a buyer. A member is told a sale happened, what it earned
 * them and when — never who bought it.
 */
export default function AffiliateEarningsPage({
  token,
  memberToken,
  primary = "#0274F7",
}) {
  const [links, setLinks] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showLinks, setShowLinks] = useState(false);

  // Empty until the member picks one, so the server answers in whichever
  // currency they have actually earned most in rather than a guess from here.
  const [currency, setCurrency] = useState("");

  // Thirty products and eighty sales are two lists, not two scrolls. Page
  // state is held here rather than inside each list so switching currency can
  // put both back to the top — page 3 of the dollar products is not page 3 of
  // the rupee ones.
  const [productPage, setProductPage] = useState(1);
  const [salePage, setSalePage] = useState(1);

  const load = useCallback(async () => {
    if (!token || !memberToken) {
      setLoading(false);

      return;
    }

    setLoading(true);
    try {
      // Together, because neither answer is useful without the other: the
      // figures need the links to explain where they came from, and the links
      // need the figures to be worth a page.
      const [linkData, statData] = await Promise.all([
        fetchAffiliateLinks({ token, memberToken }),
        fetchAffiliateStats({ token, memberToken, currency }),
      ]);

      setLinks(linkData);
      setStats(statData);
      setFailed(false);

      if (!currency && statData.currency) {
        // Adopt what the server chose, so the picker shows the currency the
        // figures are in rather than an empty slot.
        setCurrency(statData.currency);
      }
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token, memberToken, currency]);

  useEffect(() => {
    load();
  }, [load]);

  // A re-fetch with figures already on the screen — switching USD to INR, or
  // retrying after one failed. Told apart from the first load because the two
  // need opposite treatment: the first has nothing to show and gets the whole
  // page, this one has the previous currency's figures still on screen and
  // must not throw them away mid-read.
  const refreshing = loading && !!stats;

  /**
   * Ask the server for another currency.
   *
   * Both lists go back to page one: the rupee products are a different list in
   * a different order, so holding page 3 would land the member somewhere they
   * never scrolled to.
   */
  const switchTo = (code) => {
    if (code === currency) return;

    setProductPage(1);
    setSalePage(1);
    setCurrency(code);
  };

  if (loading && !stats) {
    return (
      <Frame primary={primary}>
        <div className="flex justify-center py-16">
          <InlineLoader label="Loading your earnings…" primary={primary} />
        </div>
      </Frame>
    );
  }

  if (failed && !stats) {
    return (
      <Frame primary={primary}>
        <div className="px-5 py-14 text-center sm:px-8">
          <p className="text-[15px] font-semibold text-gray-900">
            We couldn&rsquo;t load your earnings
          </p>
          <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-gray-500">
            Nothing is lost — every sale is still counted. Try again in a
            moment.
          </p>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{ background: primary }}
            className="mt-5 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-95 active:scale-[0.99] disabled:opacity-60"
          >
            {loading && <Spinner size={14} />}
            {loading ? "Trying…" : "Try again"}
          </button>
        </div>
      </Frame>
    );
  }

  const hasLinks = links?.links?.length > 0;

  // Nothing to share and nothing shared: not an error, just a member who has
  // not started. The page says how to, rather than showing a wall of zeroes.
  if (!hasLinks) {
    return (
      <>
        <Frame primary={primary}>
          <div className="px-5 py-14 text-center sm:px-8">
            <span
              className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl"
              style={{ background: `${primary}14`, color: primary }}
            >
              <LinkIcon className="h-7 w-7" />
            </span>
            <p className="mt-4 text-[15px] font-semibold text-gray-900">
              {links?.can_opt_in
                ? "You haven’t started sharing yet"
                : "Nothing to share yet"}
            </p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-gray-500">
              {links?.can_opt_in
                ? "Get your links, share them with anyone, and whatever they buy earns you a commission. It shows up here."
                : "Nothing you have bought comes with products to share. When it does, your links and your earnings will appear here."}
            </p>
            {links?.can_opt_in && (
              <button
                type="button"
                onClick={() => setShowLinks(true)}
                style={{ background: primary }}
                className="mt-5 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-95 active:scale-[0.99]"
              >
                Get my links
              </button>
            )}
          </div>
        </Frame>

        {showLinks && (
          <AffiliateEarnModal
            token={token}
            memberToken={memberToken}
            primary={primary}
            onClose={() => setShowLinks(false)}
            onChanged={load}
          />
        )}
      </>
    );
  }

  const { summary, products, recent, currencies } = stats;
  const sold = summary.sales > 0;
  const linkCount = links?.links?.length || 0;

  // The currency the member ASKED for, which during a switch is not yet the
  // one the figures are in. The picker follows the request so the pill they
  // pressed lights up straight away; every figure stays labelled with
  // `stats.currency`, the one it is actually in.
  const picked = currency || stats.currency;

  // What a figure that is about to be replaced looks like. Not interactive
  // while it is: paging through a list that is being swapped underneath is a
  // page number that lands on the wrong rows.
  const stale = refreshing
    ? "pointer-events-none opacity-45 transition-opacity duration-200"
    : "transition-opacity duration-200";

  // Thirty products is a list, not a page. Eighty sales even less so.
  const productPages = Math.ceil(products.length / PRODUCTS_PER_PAGE) || 1;
  const onProductPage = Math.min(productPage, productPages);
  const productFrom = (onProductPage - 1) * PRODUCTS_PER_PAGE;
  const shown = products.slice(productFrom, productFrom + PRODUCTS_PER_PAGE);

  const salePages = Math.ceil(recent.length / SALES_PER_PAGE) || 1;
  const onSalePage = Math.min(salePage, salePages);
  const saleFrom = (onSalePage - 1) * SALES_PER_PAGE;
  const sales = recent.slice(saleFrom, saleFrom + SALES_PER_PAGE);

  // Measured across every product, not just the page on screen, so a bar means
  // the same thing on page 4 as it does on page 1.
  const biggest = products.reduce(
    (max, item) => Math.max(max, Number(item.earned) || 0),
    0,
  );

  return (
    <div className="w-full space-y-5">
      {/* ─────────────────────────────────────────────────── the headline */}
      <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-gray-200 shadow-sm">
        <div
          className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 px-5 py-5 sm:px-7"
          style={{
            background: `linear-gradient(135deg, ${primary}14 0%, transparent 70%)`,
          }}
        >
          <div className="min-w-0">
            <h1 className="text-[17px] font-bold leading-tight text-gray-900 sm:text-[19px]">
              My earnings
            </h1>
            <p className="mt-1 text-[13px] text-gray-500">
              {sold
                ? `From ${summary.sales} sale${summary.sales === 1 ? "" : "s"}${
                    summary.products > 1
                      ? ` across ${summary.products} products`
                      : ""
                  }.`
                : "From the links you share."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Shown only when the member has actually earned in more than
                one: a single-currency picker is a control that does nothing. */}
            {currencies.length > 1 && (
              <div
                className="inline-flex rounded-xl bg-gray-100 p-0.5"
                aria-busy={refreshing}
              >
                {currencies.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => switchTo(code)}
                    // Switching is a round trip to the server, and a second
                    // press mid-flight would leave the pills and the figures
                    // describing different currencies.
                    disabled={refreshing}
                    aria-pressed={code === picked}
                    style={code === picked ? { color: primary } : undefined}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-bold transition disabled:cursor-not-allowed ${
                      code === picked
                        ? "bg-surface shadow-sm"
                        : "text-gray-500 hover:text-gray-900 disabled:text-gray-400"
                    }`}
                  >
                    {/* On the pill itself, because that is where the member is
                        looking when they press it. */}
                    {refreshing && code === picked && <Spinner size={11} />}
                    {code}
                  </button>
                ))}
              </div>
            )}

            {/* The links keep their popup: a member gets them once, copies
                them and closes it. This page is what they come back to.

                Not drawn before the first sale, because the empty state below
                already carries the same button in the middle of the panel —
                where the member is looking — and two buttons opening one popup
                on one screen is the thing that was just taken out of the
                account menu. */}
            {sold && (
              <button
                type="button"
                onClick={() => setShowLinks(true)}
                style={{ background: primary }}
                className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-95 active:scale-[0.99]"
              >
                <LinkIcon className="h-4 w-4" />
                My sharing links
              </button>
            )}
          </div>
        </div>

        {/* A switch that failed, with the previous currency's figures still on
            screen. Said out loud, because silently leaving USD up after a
            member pressed INR reads as the figures being wrong. */}
        {failed && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-100 bg-amber-50 px-5 py-2.5 sm:px-7">
            <p className="text-[12.5px] text-amber-900">
              We couldn&rsquo;t refresh these just now. You&rsquo;re seeing the
              last figures we loaded.
            </p>
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              className="text-[12.5px] font-bold text-amber-900 underline disabled:opacity-60"
            >
              {refreshing ? "Trying…" : "Try again"}
            </button>
          </div>
        )}

        {!sold ? (
          /* Signed up, links in hand, nothing sold. The commonest state this
             page will ever be in, and the one most likely to be read as
             broken — so it is drawn and explained rather than left as a blank
             panel or a row of zeroes. */
          <div className="px-5 py-11 text-center sm:px-8">
            <NoSalesArt primary={primary} />
            <p className="mt-4 text-[15px] font-semibold text-gray-900">
              No earnings yet
            </p>
            <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-500">
              {linkCount > 0 && (
                <>
                  Your {linkCount} sharing link{linkCount === 1 ? "" : "s"}{" "}
                  {linkCount === 1 ? "is" : "are"} ready.{" "}
                </>
              )}
              The moment somebody buys through one of your links, it&rsquo;ll
              show up here — what the sale earned you, and when.
            </p>
            <button
              type="button"
              onClick={() => setShowLinks(true)}
              style={{ background: primary }}
              className="mt-5 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-95 active:scale-[0.99]"
            >
              <LinkIcon className="h-4 w-4" />
              {linkCount > 0 ? "Share a link" : "Get my links"}
            </button>
            <p className="mt-2.5 text-[12px] text-gray-400">
              Nothing to set up. Share a link and check back.
            </p>
          </div>
        ) : (
          /* Dimmed rather than replaced: these are last currency's figures and
             they are about to be wrong, but blanking a page a member is in the
             middle of reading is worse than fading it. */
          <div className={stale} aria-busy={refreshing}>
            <div className="grid grid-cols-2 gap-px bg-gray-100 lg:grid-cols-4">
              <Tile
                label="Earned"
                value={money(summary.earned, stats.currency)}
                hint="All time"
                accent={primary}
              />
              <Tile
                label="Paid to you"
                value={money(summary.paid, stats.currency)}
                hint={summary.paid > 0 ? "Already sent" : "Nothing sent yet"}
              />
              {/* Said plainly, because the gap between the first two is the
                  question a member otherwise asks support. */}
              <Tile
                label="On the way"
                value={money(summary.pending, stats.currency)}
                hint="Yours, not sent yet"
              />
              <Tile
                label="Sales"
                value={String(summary.sales)}
                hint={`${summary.products} product${
                  summary.products === 1 ? "" : "s"
                }`}
              />
            </div>

            {/* One bar instead of a third figure nobody asked for: how much of
                what they have earned has actually reached them. */}
            {summary.earned > 0 && (
              <div className="px-5 py-4 sm:px-7">
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full transition-[width] duration-500"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(0, (summary.paid / summary.earned) * 100),
                      )}%`,
                      background: primary,
                    }}
                  />
                </div>
                <p className="mt-2 text-[12px] text-gray-500">
                  {summary.paid > 0
                    ? `${money(summary.paid, stats.currency)} of ${money(
                        summary.earned,
                        stats.currency,
                      )} has reached you so far.`
                    : "We’ll ask where to send your money before the first payout."}
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ────────────────────────────────────── what is actually selling */}
      {products.length > 0 && (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-gray-200 shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-7">
            <h2 className="text-[14px] font-bold text-gray-900">
              What&rsquo;s selling
            </h2>
            <span className="text-[12px] text-gray-400">
              {products.length} product{products.length === 1 ? "" : "s"}
            </span>
          </div>

          <ul className={`divide-y divide-gray-100 ${stale}`}>
            {shown.map((item, offset) => (
              <li key={item.product_id} className="px-5 py-3.5 sm:px-7">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="flex min-w-0 items-baseline gap-2.5">
                    {/* Ranked, because the question this list answers is
                        "which of my links is worth sharing again" — and ranked
                        against the whole list, so page 2 starts at 9 rather
                        than starting over at 1. */}
                    <span className="w-6 shrink-0 text-right text-[12px] font-semibold text-gray-300 tabular-nums">
                      {productFrom + offset + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[13.5px] font-medium text-gray-900">
                        {item.product_title}
                      </span>
                      <span className="text-[12px] text-gray-400">
                        {item.sales} sale{item.sales === 1 ? "" : "s"}
                      </span>
                    </span>
                  </span>
                  <span className="shrink-0 text-[13.5px] font-bold text-gray-900 tabular-nums">
                    {money(item.earned, stats.currency)}
                  </span>
                </div>

                {/* Each bar is a share of the BIGGEST earner, so the list
                    reads at a glance without anybody doing the division. */}
                {biggest > 0 && (
                  <div className="mt-2 ms-[34px] h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max(
                          2,
                          ((Number(item.earned) || 0) / biggest) * 100,
                        )}%`,
                        background: `${primary}${
                          productFrom + offset === 0 ? "" : "99"
                        }`,
                      }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>

          <ListFooter
            from={productFrom}
            count={shown.length}
            total={products.length}
            noun="products"
            page={onProductPage}
            totalPages={productPages}
            onChange={setProductPage}
            primary={primary}
            busy={refreshing}
          />
        </section>
      )}

      {/* ──────────────────────────────────────────────── the recent sales */}
      {recent.length > 0 && (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-gray-200 shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4 sm:px-7">
            <h2 className="text-[14px] font-bold text-gray-900">
              Recent sales
            </h2>
            <p className="mt-0.5 text-[12px] text-gray-400">
              Your latest {recent.length}. We never show who bought.
            </p>
          </div>

          <ul className={`divide-y divide-gray-100 ${stale}`}>
            {sales.map((item, offset) => (
              <li
                key={`${item.date}-${saleFrom + offset}`}
                className="flex items-center justify-between gap-3 px-5 py-3.5 sm:px-7"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[13.5px] font-medium text-gray-900">
                    {item.product_title}
                  </span>
                  <span className="text-[12px] text-gray-400">
                    {whenever(item.date)}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="text-[13.5px] font-bold text-gray-900 tabular-nums">
                    {money(item.earned, stats.currency)}
                  </span>
                  <Status paid={item.paid} />
                </span>
              </li>
            ))}
          </ul>

          <ListFooter
            from={saleFrom}
            count={sales.length}
            total={recent.length}
            noun="sales"
            page={onSalePage}
            totalPages={salePages}
            onChange={setSalePage}
            primary={primary}
            busy={refreshing}
          />
        </section>
      )}

      {showLinks && (
        <AffiliateEarnModal
          token={token}
          memberToken={memberToken}
          primary={primary}
          onClose={() => setShowLinks(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

/**
 * How many rows each list shows at a time.
 *
 * Eight products because the question "which of my links is worth sharing
 * again" is answered by the top of the list, and ten sales because that is
 * about a fortnight for somebody selling steadily — one screen, in both cases,
 * on a phone as well as a desktop.
 */
const PRODUCTS_PER_PAGE = 8;
const SALES_PER_PAGE = 10;

/**
 * The bar under a paged list: which rows these are, and how to reach the rest.
 *
 * Says the range as well as showing the pages because the two lists are not in
 * the same order — one is ranked by what earned most, the other by when it
 * happened — and "9–16 of 30" is what tells a member which of the two they are
 * partway through.
 *
 * Renders nothing at all when everything fits on one page: a control that
 * cannot be pressed is noise at the bottom of a short list.
 */
function ListFooter({
  from,
  count,
  total,
  noun,
  page,
  totalPages,
  onChange,
  primary,
  busy,
}) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 sm:px-7">
      <p className="text-[12px] text-gray-400 tabular-nums">
        {from + 1}–{from + count} of {total} {noun}
      </p>
      {/* `className` empty because Pagination's own default drops it eight
          rems below whatever is above it — right under a grid of courses,
          far too much inside a card footer. */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onChange={busy ? () => {} : onChange}
        primary={primary}
        className=""
      />
    </div>
  );
}

/** The card every one-state version of the page sits in. */
function Frame({ primary, children }) {
  return (
    <div className="w-full overflow-hidden rounded-2xl bg-surface ring-1 ring-gray-200 shadow-sm">
      <div
        className="border-b border-gray-100 px-5 py-5 sm:px-7"
        style={{
          background: `linear-gradient(135deg, ${primary}14 0%, transparent 70%)`,
        }}
      >
        <h1 className="text-[17px] font-bold leading-tight text-gray-900 sm:text-[19px]">
          My earnings
        </h1>
        <p className="mt-1 text-[13px] text-gray-500">
          From the links you share.
        </p>
      </div>
      {children}
    </div>
  );
}

function Tile({ label, value, hint, accent }) {
  return (
    <div className="bg-surface px-5 py-4 sm:px-7">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p
        className="mt-1 truncate text-[20px] font-bold tabular-nums"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-[11.5px] text-gray-400">{hint}</p>}
    </div>
  );
}

/**
 * Paid, or not yet.
 *
 * A badge rather than a word, so the state of a sale reads down the column
 * without being read one row at a time.
 */
function Status({ paid }) {
  return paid ? (
    <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-bold text-green-700">
      Paid
    </span>
  ) : (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500">
      On the way
    </span>
  );
}

/**
 * The chart a member does not have yet.
 *
 * Drawn rather than written, because the thing they came for is a figure and
 * a panel of prose where a figure should be reads as something having gone
 * wrong. Three dashed columns on a baseline say "this is where it goes" — the
 * outline of the chart they are about to have.
 *
 * Every column is dashed and empty. A solid one would be a picture of a sale
 * that has not happened, on the page whose whole job is to be trusted about
 * money.
 *
 * Inline SVG, like every other mark on this page: an <img> is a second request
 * that can fail, and a broken image on a page about somebody's earnings is
 * worse than no image at all.
 */
function NoSalesArt({ primary }) {
  const columns = [
    { x: 24, top: 58 },
    { x: 54, top: 45 },
    { x: 84, top: 29 },
  ];

  return (
    <svg
      viewBox="0 0 132 92"
      className="mx-auto h-[84px] w-[122px]"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="66" cy="44" r="43" fill={`${primary}0a`} />
      {columns.map(({ x, top }) => (
        <rect
          key={x}
          x={x}
          y={top}
          width="24"
          height={79 - top}
          rx="6"
          fill={`${primary}12`}
          stroke={primary}
          strokeOpacity="0.38"
          strokeWidth="2"
          strokeDasharray="5 5"
        />
      ))}
      <path
        d="M12 79h108"
        stroke={primary}
        strokeOpacity="0.22"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LinkIcon({ className }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.828 10.172a4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}
